import SEOHead, {
  generateOrganizationSchema,
  generateWebPageSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
} from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials, faqs } from '@/mocks/data';

export default function AboutPage() {
  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const orgSchema = generateOrganizationSchema();
  const pageSchema = generateWebPageSchema(
    '关于我们 - 日韩影视网 | 品牌介绍与联系方式',
    '日韩影视网品牌介绍、用户口碑与联系方式。了解我们的使命与服务理念，免费日韩电影在线观看平台。',
    'https://www.rihanyingshi.com/about',
  );
  const faqSchema = generateFAQSchema(faqs);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: '首页', url: 'https://www.rihanyingshi.com' },
    { name: '关于我们', url: 'https://www.rihanyingshi.com/about' },
  ]);

  const combinedSchema = { ...pageSchema, ...orgSchema, ...faqSchema, ...breadcrumbSchema };

  return (
    <>
      <SEOHead
        title="关于我们 - 日韩影视网 | 品牌介绍、用户口碑与联系方式"
        description="日韩影视网品牌介绍、用户口碑与联系方式。免费日韩电影在线观看平台，提供高清日本电影和韩国电影。了解我们的使命与服务理念。"
        keywords="日韩影视网,关于我们,品牌介绍,联系方式,日韩电影平台"
        canonicalUrl="https://www.rihanyingshi.com/about"
        schema={combinedSchema}
        lastModified="2026-08-03"
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-8 md:py-12">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-3">关于日韩影视网</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-[700px] mx-auto">
                致力于打造最优质的日韩电影免费在线观看平台
              </p>
            </div>

            <div className="max-w-[800px] mx-auto mb-16">
              <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-6">
                {siteInfo.about.intro}
              </p>
              <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                {siteInfo.about.mission}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {siteInfo.about.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-5 rounded-lg bg-background-800/50 border border-background-700/50"
                >
                  <div className="text-2xl md:text-3xl font-bold text-accent-400 font-heading mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-700">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">我们的优势</h2>
              <p className="text-sm text-foreground-500">为什么选择日韩影视网？</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
              {siteInfo.about.features.map((feat) => (
                <div
                  key={feat.title}
                  className="p-5 rounded-lg bg-background-800/50 border border-background-700/50 hover:border-primary-500/30 transition-all duration-300"
                >
                  <h3 className="text-sm font-bold text-foreground-100 mb-2">{feat.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-700">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500">听听影迷们怎么说</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-16">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-lg bg-background-800/50 border border-background-700/50"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-200">{t.name}</div>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-600'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-400 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-700">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">常见问题</h2>
              <p className="text-sm text-foreground-500">关于日韩影视网的常见疑问解答</p>
            </div>

            <div className="max-w-[800px] mx-auto space-y-3 mb-16">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-800/50 border border-background-700/50 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-4 text-sm font-medium text-foreground-200 hover:text-foreground-100 transition-colors cursor-pointer">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-4 pb-4 text-xs text-foreground-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-700">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">联系我们</h2>
              <p className="text-sm text-foreground-500">欢迎通过以下方式与我们取得联系</p>
            </div>

            <div className="max-w-[600px] mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="flex flex-col items-center p-5 rounded-lg bg-background-800/50 border border-background-700/50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">电子邮箱</span>
                  <a href="mailto:contact@rihanyingshi.com" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
                    contact@rihanyingshi.com
                  </a>
                </div>

                <div className="flex flex-col items-center p-5 rounded-lg bg-background-800/50 border border-background-700/50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-400 mb-3">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">微信公众号</span>
                  <span className="text-sm text-foreground-300">日韩影视网</span>
                </div>

                <div className="flex flex-col items-center p-5 rounded-lg bg-background-800/50 border border-background-700/50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500/10 text-secondary-400 mb-3">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">工作时间</span>
                  <span className="text-sm text-foreground-300">周一至周五 9:00-18:00</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}